import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router';
import schedulingClient from '../../lib/scheduling/client';
import {
  AppointmentTypeWithAvailableSlots,
  GroupClassFilteringCategory,
} from '../../lib/scheduling/types';
import { getGroupClassFilters, storeGroupClassFilters } from '../../lib/storage';
import MixpanelClient, { TrackingEventV2 } from '../../lib/tracking/mixpanel';
import userClient from '../../lib/user/client';
import { StyledTheme } from '../../theme';
import Authenticating from '../Common/Authenticating/Authenticating';
import Prompt from '../Common/Prompt/Prompt';
import CoreLayout from '../Layout/CoreLayout';
import GroupClassCard from './GroupClassCard';
import './group-class-card.css';

const GroupClasses: FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<GroupClassFilteringCategory | 'all'>(
    getGroupClassFilters()?.category || 'all'
  );
  const [groupClassAvailability, setGroupClassAvailability] = useState<
    AppointmentTypeWithAvailableSlots[]
  >([]);
  const [fetchingStatus, setFetchingStatus] = useState<'authenticating' | 'fetching' | 'done'>(
    'authenticating'
  );

  const { token, ...searchParams } = useParams<{ token: string; src: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      return;
    }

    (async () => {
      if (!(await userClient.getProfileWithToken(token))) {
        return navigate(`/error`);
      }

      MixpanelClient.trackEvent({
        eventName: TrackingEventV2.GroupClassListClasses,
        properties: { ...searchParams, source: searchParams.src || '' },
      });

      setFetchingStatus('fetching');

      const groupClassAvailability = await schedulingClient.getGroupClassesWithAvailability(
        selectedCategory
      );

      if (!groupClassAvailability) {
        return navigate(`/error`);
      }

      setGroupClassAvailability(groupClassAvailability);
      setFetchingStatus('done');
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const handleChangeCategory = async (
    event: SelectChangeEvent<GroupClassFilteringCategory | 'all'>
  ): Promise<void> => {
    const {
      target: { value },
    } = event;

    const category = value as GroupClassFilteringCategory | 'all';

    storeGroupClassFilters({ category });

    setSelectedCategory(category);
    setFetchingStatus('fetching');

    const groupClassAvailability = await schedulingClient.getGroupClassesWithAvailability(category);

    if (groupClassAvailability) {
      setGroupClassAvailability(groupClassAvailability);
    }

    setFetchingStatus('done');
  };

  return (
    <>
      <Helmet>
        <title>Enara: Book your group class</title>
      </Helmet>

      {fetchingStatus === 'authenticating' ? (
        <Authenticating />
      ) : fetchingStatus === 'fetching' ? (
        <Prompt
          title={'Group class availability'}
          content={'We are verifying and downloading availability for group classes.'}
        />
      ) : (
        <CoreLayout containerClassName={'gc-container'} logo={false}>
          <Box display={'flex'} alignItems={'center'} width={'100%'}>
            <IconButton href='com.enara://' style={{ color: 'black' }}>
              <ChevronLeftIcon fontSize='large' />
            </IconButton>

            <Typography variant='h2'>Group Classes</Typography>
          </Box>

          <Box width={'90%'} display={'flex'} flexDirection={'column'} gap={1}>
            <Typography variant='h4'>Filter by</Typography>

            <FormControl variant='standard'>
              <Select
                label='Select Category'
                onChange={handleChangeCategory}
                sx={{
                  color: StyledTheme.colors.purple,
                  '& .MuiSvgIcon-root': {
                    color: StyledTheme.colors.purple,
                  },
                  '&:before': {
                    borderColor: 'transparent',
                  },
                  '&:after': {
                    borderColor: 'transparent',
                  },
                }}
                value={selectedCategory}>
                <MenuItem value={GroupClassFilteringCategory.BehavioralHealth}>
                  Behavioral Health
                </MenuItem>
                <MenuItem value={GroupClassFilteringCategory.Exercise}>Exercise</MenuItem>
                <MenuItem value={GroupClassFilteringCategory.Nutrition}>Nutrition</MenuItem>
                <MenuItem value={GroupClassFilteringCategory.Orientations}>Orientations</MenuItem>
                <MenuItem value={'all'}>All</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {groupClassAvailability.map((appointmentTypeWithSlots, i) => (
            <GroupClassCard
              key={`group-class-${i}`}
              index={i}
              appointmentTypeWithSlots={appointmentTypeWithSlots}
              onRegister={(appointment) => {
                navigate(`/group-classes/${appointment.id}/detail`, { state: appointment });
              }}
            />
          ))}
        </CoreLayout>
      )}
    </>
  );
};

export default GroupClasses;
